import React, { useState, Fragment } from 'react'
import {
	isFreeResponse,
	isFreeResponseAnswer,
	QuestionOptionIcon,
	Image,
} from '../../utils'
import { FreeResponseAnswer, validate } from '@mission.io/question-toolkit'
import TeacherHeader from '../../components/teacher-header/TeacherHeader'
import { TeacherQuestionProps } from '../Question'
import AnalyticsSingleStudentContainer from '../../components/analytics-single-student/AnalyticsSingleStudent'
import Collapsible from '../../components/collapsible'
import { HeaderContent } from '../image-labeling/Teacher'
import EditFooter from '../../components/edit-footer'
import {
	ReadOnlyFormInput,
	ReadOnly,
	QuestionOption,
	QuestionResponseStyled,
	ReadOnlyChoiceAnswerMarker,
} from '../../assets/styles'
import 'styled-components/macro'
// @ts-expect-error The styled-components babel plugin transforms the code to use the default export from styled-components (ie `import styled from styled-components`). Using the default export is broken in ESM, so we have to manually import `styled` to avoid this behavior.
// eslint-disable-next-line  @typescript-eslint/no-unused-vars
import { styled } from 'styled-components'
import { StudentResponse } from '../../model/general'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'

/**
 * A component for a teacher View where can create the free response question. It also has a readOnly view
 * where it can't be edited
 * @returns {JSX.Element}
 */
export const TeacherFreeResponse = ({
	question,
	readOnly = false,
	onChange,
	studentResponse,
	questionIndex = 0,
	isQuestionAnalytics,
	studentName,
	onRequestClose,
	isCollapsibleQuestion,
	buttonAction,
	autoFocus = false,
}: TeacherQuestionProps & {
	studentResponse?: StudentResponse<FreeResponseAnswer> | null
}): JSX.Element => {
	if (!question || !isFreeResponse(question)) return <Fragment />

	const isValid = !validate(question)?.error

	if (isQuestionAnalytics)
		return onRequestClose ? (
			<AnalyticsSingleStudentContainer
				question={question}
				title="Free Response Question"
				studentResponse={studentResponse}
				onRequestClose={onRequestClose}
				studentName={studentName}>
				<QuestionResponse studentResponse={studentResponse} />
			</AnalyticsSingleStudentContainer>
		) : (
			<Fragment />
		)

	const [expand, setExpand] = useState(false)

	if (isCollapsibleQuestion)
		return (
			<div>
				<Collapsible
					handleClick={() => setExpand(state => !state)}
					expand={expand}
					header={
						<HeaderContent>
							<div css="display: inline-block; flex: 1;">
								<span className="notranslate">Q:&nbsp;</span>
								<OptionalMarkdown className="prose-invert">
									{question.phrase}
								</OptionalMarkdown>
							</div>
							<Image media={question.media} expandable />
						</HeaderContent>
					}
					buttonAction={buttonAction}>
					<div>
						<QuestionOption $isReadOnly={true} $chosen={false} $correct={true}>
							<QuestionOptionIcon studentChoice={false} correctOption={true} />
							<span>Free Response Answer</span>
						</QuestionOption>
					</div>
				</Collapsible>
			</div>
		)

	return readOnly ? (
		<ReadOnly className="Question">
			{question && (
				<TeacherHeader
					darkMode={true}
					questionIndex={questionIndex}
					isValid={isValid}
					question={question}
					studentResponse={studentResponse}
					readOnly={readOnly}
				/>
			)}
			<ReadOnlyFormInput>
				<ReadOnlyChoiceAnswerMarker />
				<div className="question-choice" css="font-style: italic">
					No answer: This is a free response
				</div>
			</ReadOnlyFormInput>
		</ReadOnly>
	) : (
		<div>
			<TeacherHeader
				readOnly={readOnly}
				label="Free Response"
				question={question}
				questionIndex={questionIndex}
				onChange={onChange}
				autoFocus={autoFocus}
				inputType="textarea"
			/>
			<EditFooter onChange={onChange} />
		</div>
	)
}

/**
 * This function makes the answer box for Analytics Student answer.
 * We just output the student answer.
 */
function QuestionResponse({
	studentResponse,
}: {
	studentResponse?: StudentResponse<FreeResponseAnswer> | null
}): JSX.Element {
	if (studentResponse && !isFreeResponseAnswer(studentResponse.answer))
		return <Fragment />

	const answer = studentResponse?.answer?.value || null

	const isCorrect =
		studentResponse?.score?.score == null
			? null
			: studentResponse?.score?.score === studentResponse?.score?.perfectScore

	return (
		<QuestionResponseStyled>
			<QuestionOption $correct={isCorrect} $chosen={isCorrect === false}>
				<QuestionOptionIcon
					correctOption={Boolean(isCorrect)}
					studentChoice={isCorrect === false}
				/>
				{studentResponse?.answer ? answer : 'Did not answer'}
			</QuestionOption>
		</QuestionResponseStyled>
	)
}

export default TeacherFreeResponse
